import './cartView.scss';

import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useCart } from '~hooks';
import {
  CartEmpty,
  CartFooter,
  ConfirmDialog,
  type ConfirmDialogPreview,
  ProductGroup,
} from '~molecules';
import { clearCart, removeParticipant, removeProduct } from '~redux/reducers/cartSlice';
import type { Simulation } from '~types/appsync.types';
import type { CartProduct, ProductDisplay } from '~types/cart';

export interface CartViewProps {
  onCheckout: (products: CartProduct[], simulation: Simulation | undefined) => void;
}

type ConfirmState = { type: 'removeProduct'; productId: string } | { type: 'clearCart' } | null;

interface ConfirmContent {
  body: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  preview?: ConfirmDialogPreview;
  title: string;
}

const toPreview = (product: ProductDisplay | undefined): ConfirmDialogPreview | undefined =>
  product && {
    category: product.productCategoryName,
    title: product.validityCategoryLabel,
  };

const CartView = ({ onCheckout }: CartViewProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { productDisplays, isPriceLoading, products, simulation, error } = useCart();

  const [confirm, setConfirm] = useState<ConfirmState>(null);

  const handleRemoveParticipant = useCallback(
    (productId: string, participantId: number) => {
      dispatch(removeParticipant({ participantId, productId }));
    },
    [dispatch],
  );

  const closeConfirm = useCallback(() => setConfirm(null), []);

  const handleCheckout = useCallback(() => {
    onCheckout(products, simulation);
  }, [onCheckout, products, simulation]);

  const confirmContent = useMemo<ConfirmContent | null>(() => {
    if (!confirm) return null;

    if (confirm.type === 'removeProduct') {
      return {
        body: (
          <FormattedMessage
            defaultMessage='All participants and options will be removed. <strong>This action cannot be undone.</strong>'
            id='cartRemoveProductBody'
            values={{ strong: (chunks) => <strong>{chunks}</strong> }}
          />
        ),
        confirmLabel: intl.formatMessage({
          defaultMessage: 'Remove this product',
          id: 'cartRemoveProductConfirm',
        }),
        onConfirm: () => {
          dispatch(removeProduct(confirm.productId));
          closeConfirm();
        },
        preview: toPreview(productDisplays.find((d) => d.productId === confirm.productId)),
        title: intl.formatMessage({
          defaultMessage: 'Remove this product from the cart?',
          id: 'cartRemoveProductTitle',
        }),
      };
    }

    return {
      body: (
        <FormattedMessage
          defaultMessage='All products and participants will be removed. You will need to rebuild your selection.'
          id='cartClearBody'
        />
      ),
      confirmLabel: intl.formatMessage({
        defaultMessage: 'Empty cart',
        id: 'cartClearConfirm',
      }),
      onConfirm: () => {
        dispatch(clearCart());
        closeConfirm();
      },
      title: intl.formatMessage({
        defaultMessage: 'Empty the entire cart?',
        id: 'cartClearTitle',
      }),
    };
  }, [confirm, productDisplays, dispatch, intl, closeConfirm]);

  if (products.length === 0) {
    return (
      <div className='CartView CartView-empty'>
        <CartEmpty />
      </div>
    );
  }

  const currency = simulation?.currency ?? 'EUR';
  const totalInCents = simulation?.prices.total ?? 0;

  const hasError = !!error;

  return (
    <div className='CartView'>
      {hasError && (
        <div className='CartView_error' role='alert'>
          <FormattedMessage
            defaultMessage='We could not compute the cart total. Please review the items below and try again.'
            id='cartSimulationError'
          />
        </div>
      )}
      <div className='CartView_body'>
        {productDisplays.map((display) => (
          <ProductGroup
            isPriceLoading={isPriceLoading}
            key={display.productId}
            onRemoveParticipant={handleRemoveParticipant}
            onRemoveProduct={(productId) => setConfirm({ productId, type: 'removeProduct' })}
            product={display}
          />
        ))}
      </div>
      <CartFooter
        currency={currency}
        hasError={hasError}
        isPriceLoading={isPriceLoading}
        onCheckout={handleCheckout}
        onClear={() => setConfirm({ type: 'clearCart' })}
        totalInCents={totalInCents}
      />
      {confirmContent && (
        <ConfirmDialog
          body={confirmContent.body}
          confirmLabel={confirmContent.confirmLabel}
          isOpen
          onCancel={closeConfirm}
          onConfirm={confirmContent.onConfirm}
          preview={confirmContent.preview}
          title={confirmContent.title}
          variant='danger'
        />
      )}
    </div>
  );
};

export default CartView;
